import {useCampaignPaidDeliveryQuery} from "@/api/paidDeliveryApi";
import LockIcon from "@/assets/colorable/lock.svg?react";
import BoostMediaTable from "@/components/BoostEstimate/BoostMediaTable";
import BoostMethodCard from "@/components/BoostEstimate/BoostMethodCard";
import BoostPlatformCard from "@/components/BoostEstimate/BoostPlatformCard";
import FullPageLoader from "@/components/Common/FullPageLoader";
import {createFileRoute, Link} from "@tanstack/react-router";
import {ChevronLeft} from "lucide-react";
import {useTranslation} from "react-i18next";
import {useTitle} from "react-use";

export const Route = createFileRoute("/_authenticated/_admin/admins/boost-estimate/$campaignId")({
  component: BoostEstimatePage,
});

function BoostEstimatePage() {
  const {t} = useTranslation("boost_estimate");

  useTitle(t("title"));

  const {campaignId} = Route.useParams();
  const {data, isError, isLoading} = useCampaignPaidDeliveryQuery(Number(campaignId));

  if (isLoading) return <FullPageLoader />;

  return (
    <div className="max-w-wide container mx-auto mt-6 mb-20 flex min-w-0 flex-col gap-6 px-4 sm:mt-8 sm:px-8">
      <div className="flex flex-col gap-1">
        <Link
          to="/admins/boost-estimate"
          className="text-neutral-grey-dark hover:text-neutral-black mb-1 flex w-fit items-center gap-1 text-sm no-underline">
          <ChevronLeft aria-hidden="true" className="size-4" />
          {t("all_campaigns")}
        </Link>
        <div className="flex flex-wrap items-center gap-3">
          <h1 className="bg-linear-(--primary-dark-gradient) bg-clip-text text-2xl font-bold text-transparent">
            {t("title")}
          </h1>
          <span className="bg-warning-light-yellow text-warning-dark-yellow text-2xs flex items-center gap-1.5 rounded-full px-2.5 py-1 font-semibold">
            <LockIcon aria-hidden="true" className="size-3 shrink-0" />
            {t("internal_badge")}
          </span>
        </div>
        {data && (
          <span className="text-neutral-grey-dark text-sm">
            {[data.company_name, data.campaign_title, `#${data.campaign_id}`].filter(Boolean).join(" · ")}
          </span>
        )}
      </div>
      {isError && <p className="text-danger-dark-red text-sm">{t("error")}</p>}
      {data && (
        <>
          <BoostMethodCard
            instagramFloor={data.unpromoted_paid_share.instagram}
            tiktokFloor={data.unpromoted_paid_share.tiktok}
          />
          {data.platforms.length ? (
            <>
              <div className="grid min-w-0 grid-cols-1 gap-4 xl:grid-cols-2">
                {data.platforms.map(platform => (
                  <BoostPlatformCard key={platform.platform} platform={platform} />
                ))}
              </div>
              <BoostMediaTable
                instagramFloor={data.unpromoted_paid_share.instagram}
                platforms={data.platforms}
                tiktokFloor={data.unpromoted_paid_share.tiktok}
              />
            </>
          ) : (
            <p className="text-neutral-grey-dark text-sm">{t("no_media")}</p>
          )}
        </>
      )}
    </div>
  );
}
